/**
 * 任务相关 API
 */

import { authenticatedFetch } from './auth';
import { appConfig } from '../config';

// ============================================================================
// 类型定义
// ============================================================================

/**
 * 漫剪任务项
 */
export interface ManjianTaskItem {
  prompt: string;           // 提示词
  imageUrl: string;         // 图片URL
  generateCount: number;    // 生成数量
  rate: string;             // 比例 如 "16:9"（已弃用，用resolution代替）
  duration: number;         // 时长（秒）：普通模式 10/15，Pro模式 10/15/25
  hasWatermark: boolean;    // 是否有水印
  model: string;            // 模型
  isPro?: boolean;         // 是否使用Pro模型（默认false）
  resolution?: string;      // 分辨率：horizontal（横屏）/vertical（竖屏）
  quality?: string;         // 清晰度：normal/high（Pro模式自动高清）
}

/**
 * 创建任务请求参数
 */
export interface CreateTaskRequest {
  taskType: string;                     // 任务类型
  manjianTaskList: ManjianTaskItem[];   // 漫剪任务列表
  priority?: number;                    // 优先级（可选）
}

/**
 * 创建任务响应
 */
export interface CreateTaskResponse {
  code: number;
  msg: string;
  data: number[];  // 返回创建的任务ID列表
}

/**
 * 任务详情（包含执行状态）
 */
export interface TaskDetail extends ManjianTaskItem {
  id?: number;
  execStatus: number;  // 执行状态: 0=待执行, 1=执行中, 2=已完成, 3=失败
  videoUrl?: string;   // 生成的视频URL（完成后）
  errorMsg?: string;   // 错误信息（失败时）
  createTime?: string; // 创建时间
  progress?: number;   // 进度 0-100
}

/**
 * 查询任务详情响应
 */
export interface GetTaskResponse {
  code: number;
  msg: string;
  data: TaskDetail;
}

/**
 * 分页参数
 */
export interface PageParams {
  pageNo?: number;           // 页码，从1开始
  pageSize?: number;         // 每页数量
  execStatusList?: number[]; // 状态筛选列表
}

/**
 * 任务列表分页响应
 */
export interface TaskPageResponse {
  code: number;
  msg: string;
  data: {
    total: number;
    list: TaskDetail[];
  };
}

// ============================================================================
// 执行状态枚举
// ============================================================================

export const TaskExecStatus = {
  FAILED: 0,      // 失败
  SUCCESS: 2,     // 成功
  PROCESSING: 3,  // 生成中/等待处理
} as const;

export const TaskExecStatusText: Record<number, string> = {
  [TaskExecStatus.FAILED]: '失败',
  [TaskExecStatus.SUCCESS]: '已完成',
  [TaskExecStatus.PROCESSING]: '生成中',
};

// 判断任务是否已结束（不需要轮询）
export const isTaskFinished = (status: number): boolean => {
  return status === TaskExecStatus.FAILED || status === TaskExecStatus.SUCCESS;
};

// ============================================================================
// API 函数
// ============================================================================

/**
 * 创建任务
 * @param data 创建任务请求参数
 * @returns 创建的任务ID列表
 */
export const createTask = async (data: CreateTaskRequest): Promise<CreateTaskResponse> => {
  const response = await authenticatedFetch(
    `${appConfig.api.baseUrl}/manjian/task/create`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

/**
 * 查询任务详情
 * @param id 任务ID
 * @returns 任务详情
 */
export const getTask = async (id: number): Promise<GetTaskResponse> => {
  const response = await authenticatedFetch(
    `${appConfig.api.baseUrl}/manjian/task/get?id=${id}`,
    {
      method: 'GET',
    }
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

/**
 * 查询任务列表（分页）
 * @param params 分页参数
 * @returns 任务列表
 */
export const getTaskPage = async (params?: PageParams): Promise<TaskPageResponse> => {
  const queryParams = new URLSearchParams();
  
  if (params?.pageNo) {
    queryParams.append('pageNo', params.pageNo.toString());
  }
  if (params?.pageSize) {
    queryParams.append('pageSize', params.pageSize.toString());
  }
  if (params?.execStatusList && params.execStatusList.length > 0) {
    // 多个状态用逗号分隔或多个参数
    params.execStatusList.forEach(status => {
      queryParams.append('execStatusList', status.toString());
    });
  }

  const queryString = queryParams.toString();
  const url = `${appConfig.api.baseUrl}/manjian/task/page${queryString ? `?${queryString}` : ''}`;

  const response = await authenticatedFetch(url, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

// ============================================================================
// 便捷函数
// ============================================================================

/**
 * 创建漫剪任务的便捷方法
 * @param tasks 任务列表
 * @param priority 优先级（默认3）
 */
export const createManjianTasks = async (
  tasks: ManjianTaskItem[],
  priority: number = 3
): Promise<CreateTaskResponse> => {
  return createTask({
    taskType: 'manjian',
    manjianTaskList: tasks,
    priority,
  });
};

/**
 * 轮询任务状态直到完成或失败
 * @param taskId 任务ID
 * @param onProgress 进度回调
 * @param interval 轮询间隔（毫秒），默认2000
 * @param timeout 超时时间（毫秒），默认300000（5分钟）
 */
export const pollTaskUntilComplete = async (
  taskId: number,
  onProgress?: (task: TaskDetail) => void,
  interval: number = 2000,
  timeout: number = 300000
): Promise<TaskDetail> => {
  const startTime = Date.now();

  return new Promise((resolve, reject) => {
    const poll = async () => {
      try {
        // 检查超时
        if (Date.now() - startTime > timeout) {
          reject(new Error('任务超时'));
          return;
        }

        const response = await getTask(taskId);
        
        if (response.code !== 0) {
          reject(new Error(response.msg || '获取任务失败'));
          return;
        }

        const task = response.data;
        
        // 回调进度
        if (onProgress) {
          onProgress(task);
        }

        // 检查状态 - 成功或失败都停止轮询
        if (task.execStatus === TaskExecStatus.SUCCESS) {
          resolve(task);
          return;
        }

        if (task.execStatus === TaskExecStatus.FAILED) {
          reject(new Error(task.errorMsg || '任务执行失败'));
          return;
        }

        // 继续轮询（生成中状态）
        setTimeout(poll, interval);
      } catch (error) {
        reject(error);
      }
    };

    poll();
  });
};

/**
 * 批量删除任务
 * @param ids 任务ID列表
 * @returns 删除结果
 */
export const deleteTaskList = async (ids: number[]): Promise<{ code: number; msg: string; data: any }> => {
  // 将 ids 作为 URL 参数传递
  const params = new URLSearchParams();
  ids.forEach(id => params.append('ids', id.toString()));
  
  const response = await authenticatedFetch(
    `${appConfig.api.baseUrl}/manjian/task/delete-list?${params.toString()}`,
    {
      method: 'DELETE',
    }
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

